import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Hero } from "@/components/sim/Hero";
import { CaseSelect } from "@/components/sim/CaseSelect";
import { CaseHeader } from "@/components/sim/CaseHeader";
import { MeterPanel } from "@/components/sim/MeterPanel";
import { ScenarioCard } from "@/components/sim/ScenarioCard";
import { BriefingCard } from "@/components/sim/BriefingCard";
import { FacilitatorNotes } from "@/components/sim/FacilitatorNotes";
import { SummaryScreen } from "@/components/sim/SummaryScreen";
import { GroundingPause, HEAVY_CASES } from "@/components/sim/GroundingPause";
import { KnowledgeCheck } from "@/components/sim/KnowledgeCheck";
import { FeedbackSurvey } from "@/components/sim/FeedbackSurvey";
import { fetchScenarios, fetchScenario, saveSession, fetchDebrief } from "@/lib/api";
import { computeTier, applyThreading, clampMeter } from "@/lib/sim";
import { loadProgress, recordCase, computeCaseScore, resetProgress, loadTraineeName, loadCohort, loadKnowledge, saveKnowledge } from "@/lib/progression";
import { useCaseAudio } from "@/hooks/useCaseAudio";

export default function Simulator() {
  const [scenarios, setScenarios] = useState([]);
  const [scenario, setScenario] = useState(null);
  const [phase, setPhase] = useState("intro");
  const [meters, setMeters] = useState(null);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [log, setLog] = useState([]);
  const [tierKey, setTierKey] = useState("solid");
  const [endedEarly, setEndedEarly] = useState(false);
  const [debrief, setDebrief] = useState(null);
  const [loadingDebrief, setLoadingDebrief] = useState(false);
  const [rank, setRank] = useState("trainee");
  const [progress, setProgress] = useState({ rank: "trainee", cases: {} });
  const [caseScore, setCaseScore] = useState(null);
  const [promotion, setPromotion] = useState(null);
  const [flags, setFlags] = useState({});
  const [sessionId, setSessionId] = useState(null);
  const [knowledge, setKnowledge] = useState(loadKnowledge());
  const [exploring, setExploring] = useState(false);

  const { soundOn, autoNarrate, toggleSound, toggleNarrate } = useCaseAudio(phase, scenario, current);

  useEffect(() => {
    fetchScenarios()
      .then(setScenarios)
      .catch(() => toast.error("Could not load cases. Please refresh."));
    const p = loadProgress();
    setProgress(p);
    setRank(p.rank);
  }, []);

  const tally = useMemo(() => ({
    good: log.filter((c) => c.tone === "good").length,
    caution: log.filter((c) => c.tone === "caution").length,
    harmful: log.filter((c) => c.tone === "harmful").length,
  }), [log]);

  const resetRun = (sc) => {
    setMeters(sc.starting_meters);
    setCurrent(0);
    setSelected(null);
    setLog([]);
    setEndedEarly(false);
    setDebrief(null);
    setLoadingDebrief(false);
    setCaseScore(null);
    setPromotion(null);
    setFlags({});
    setSessionId(null);
  };

  const startCase = async (id) => {
    try {
      const sc = await fetchScenario(id);
      setScenario(sc);
      resetRun(sc);
      setPhase("briefing");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Could not load that case.");
    }
  };

  const pickCase = (id) => {
    startCase(id);
  };

  const replaySame = () => {
    resetRun(scenario);
    setPhase("playing");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goToSelect = () => {
    setPhase("select");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleResetProgress = () => {
    const p = resetProgress();
    setProgress(p);
    setRank("trainee");
    setKnowledge({});
    toast.success("Progress reset. You are a Trainee Counselor again.");
  };

  const decision = applyThreading(scenario?.decisions[current], flags);

  const handleChoose = (i) => {
    if (selected !== null) return;
    const choice = decision.choices[i];
    if (choice.sets_flag) setFlags((f) => ({ ...f, [choice.sets_flag]: true }));
    setMeters((m) => ({
      safety: clampMeter(m.safety + choice.effects.safety),
      trust: clampMeter(m.trust + choice.effects.trust),
      time: clampMeter(m.time + choice.effects.time),
      resources: clampMeter(m.resources + choice.effects.resources),
    }));
    setLog((l) => [
      ...l,
      {
        decision_id: decision.id,
        decision_title: decision.title,
        choice_index: i,
        choice_text: choice.text,
        tone: choice.tone,
        principle: decision.principle || "",
      },
    ]);
    setSelected(i);
  };

  const finalize = async (finalMeters, finalLog, early) => {
    const harmful = finalLog.filter((c) => c.tone === "harmful").length;
    const key = computeTier(finalMeters, harmful, early);
    setTierKey(key);
    setEndedEarly(early);

    const score = computeCaseScore(finalMeters, finalLog);
    setCaseScore(score);
    if (!exploring) {
      const result = recordCase(scenario.id, score, early);
      setProgress(result.progress);
      setRank(result.newRank);
      setPromotion(result.promoted ? { oldRank: result.oldRank, newRank: result.newRank } : null);
    }

    setPhase(HEAVY_CASES.has(scenario.id) ? "grounding" : "summary");
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (exploring) return;
    setLoadingDebrief(true);
    try {
      const session = await saveSession({
        scenario_id: scenario.id,
        scenario_title: scenario.title,
        client: scenario.client,
        trainee_name: loadTraineeName(),
        cohort: loadCohort(),
        choices: finalLog,
        final_meters: finalMeters,
        outcome_tier: key,
        ended_early: early,
      });
      setSessionId(session.id);
      const res = await fetchDebrief(session.id);
      setDebrief(res.debrief);
    } catch {
      setDebrief(null);
      toast.error("Reflection coach is unavailable, but your results were saved.");
    } finally {
      setLoadingDebrief(false);
    }
  };

  const handleContinue = () => {
    if (meters.safety <= 12) {
      finalize(meters, log, true);
      return;
    }
    const isLast = current === scenario.decisions.length - 1;
    if (isLast) {
      finalize(meters, log, false);
    } else {
      setCurrent((c) => c + 1);
      setSelected(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (phase === "intro") return <Hero onStart={goToSelect} />;

  if (phase === "precheck") {
    return (
      <KnowledgeCheck
        phase="pre"
        onSubmitted={(r) => setKnowledge(saveKnowledge("pre", r))}
        onExit={goToSelect}
        onDone={goToSelect}
      />
    );
  }

  if (phase === "postcheck") {
    return (
      <KnowledgeCheck
        phase="post"
        preScore={knowledge.pre?.score ?? null}
        onSubmitted={(r) => setKnowledge(saveKnowledge("post", r))}
        onExit={goToSelect}
        onDone={() => {
          setPhase("survey");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />
    );
  }

  if (phase === "survey") {
    return <FeedbackSurvey onDone={goToSelect} />;
  }

  if (phase === "select") {
    const completedCount = Object.keys(progress.cases || {}).length;
    const allDone = scenarios.length > 0 && scenarios.every((s) => progress.cases[s.id] != null);
    return (
      <CaseSelect
        scenarios={scenarios}
        rank={rank}
        progress={progress}
        knowledge={knowledge}
        preCheckAvailable={!knowledge.pre && completedCount === 0}
        onPreCheck={() => {
          setPhase("precheck");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        postCheckReady={allDone && !knowledge.post}
        onPostCheck={() => {
          setPhase("postcheck");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        onPick={pickCase}
        onBack={() => setPhase("intro")}
        onResetProgress={handleResetProgress}
        exploring={exploring}
        onToggleExploring={setExploring}
      />
    );
  }

  if (!scenario || !meters) {
    return <div className="min-h-screen grid place-items-center text-stone-400">Loading…</div>;
  }

  if (phase === "briefing") {
    return <BriefingCard scenario={scenario} autoNarrate={autoNarrate} onStart={() => setPhase("playing")} />;
  }

  if (phase === "grounding") {
    return <GroundingPause onContinue={() => setPhase("summary")} />;
  }

  if (phase === "summary") {
    return (
      <SummaryScreen
        caseTitle={scenario.title}
        client={scenario.client}
        meters={meters}
        tierKey={tierKey}
        endedEarly={endedEarly}
        tally={tally}
        choices={log}
        caseScore={caseScore}
        rank={rank}
        promotion={promotion}
        debrief={debrief}
        loadingDebrief={loadingDebrief}
        sessionId={sessionId}
        exploring={exploring}
        onReplay={replaySame}
        onTryAnother={goToSelect}
        onHome={() => {
          setPhase("intro");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {exploring && (
          <div data-testid="exploring-banner" className="mb-6 rounded-xl border border-sky-200 bg-sky-50 px-4 py-2.5 text-sm text-sky-900">
            Exploring mode: nothing is recorded. Enter your name on the case screen to train for real.
          </div>
        )}
        <CaseHeader
          scenario={scenario}
          rank={rank}
          timeLow={meters.time <= 25}
          safetyCritical={meters.safety <= 20}
          autoNarrate={autoNarrate}
          soundOn={soundOn}
          onToggleNarrate={toggleNarrate}
          onToggleSound={toggleSound}
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-3 order-2 lg:order-1">
            <div className="lg:sticky lg:top-8">
              <MeterPanel meters={meters} timeLabel={decision.time_label} lens={decision.lens} rank={rank} />
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <ScenarioCard
              scenarioId={scenario.id}
              client={scenario.client}
              decision={decision}
              autoNarrate={autoNarrate}
              nextDecisionId={scenario.decisions[current + 1]?.id ?? null}
              index={current}
              total={scenario.decisions.length}
              selectedIndex={selected}
              onChoose={handleChoose}
              onContinue={handleContinue}
              onRestartCase={replaySame}
              onExit={goToSelect}
              isLast={current === scenario.decisions.length - 1}
              willEndEarly={selected !== null && meters.safety <= 12}
            />
          </div>
          <div className="lg:col-span-3 order-3">
            <div className="lg:sticky lg:top-8">
              <FacilitatorNotes tally={tally} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
